import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { startOfMonth } from 'date-fns';
import { GET_PORTFOLIO_TRANSACTIONS } from '../graphql/queries/transactions';
import { GET_TOKEN_LOCATIONS } from '../graphql/queries/tokenLocations';
import MonthlyTransactionGroup from '../components/MonthlyTransactionGroup';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import ExportTransactionsButton from '../components/ExportTransactionsButton';
import { useImportModal } from '../contexts/ImportModalContext';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { usePositionContext } from '../contexts/PositionContext';
import PortfolioRequired from '../components/PortfolioRequired';
import TransactionModal from '../components/TransactionModal';
import DeleteTransactionModal from '../components/DeleteTransactionModal';
import EmptyState from '../components/EmptyState';
import StockTransactionModal from '../components/StockTransactionModal';
import useStockLocations from '../hooks/useStockLocations';
import StockDeleteTransactionDialog from '../components/StockDeleteTransactionDialog';
import { GET_STOCK_PORTFOLIO_TRANSACTIONS } from '../graphql/queries/stocks';

const TransactionsPage = ({ assetKind = 'crypto' }) => {
  const isStock = assetKind === 'stock';
  const { currentPortfolio } = usePortfolioContext();
  const { positions, reloadPositions } = usePositionContext();
  const [allTransactions, setAllTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [beforeTransactionTime, setBeforeTransactionTime] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [limit, setLimit] = useState(100);
  const { openImportModal } = useImportModal();

  const { data: locationsData } = useQuery(GET_TOKEN_LOCATIONS, {
    variables: { portfolioId: currentPortfolio?.id },
    skip: !currentPortfolio?.id || isStock,
  });

  const { locations: stockLocations } = useStockLocations(currentPortfolio?.id, { skip: !isStock });
  const tokenLocations = isStock ? stockLocations : (locationsData?.tokenLocations || []);

  const { loading, error, fetchMore, refetch } = useQuery(isStock ? GET_STOCK_PORTFOLIO_TRANSACTIONS : GET_PORTFOLIO_TRANSACTIONS, {
    variables: {
      portfolioId: currentPortfolio?.id,
      limit: limit,
    },
    skip: !currentPortfolio?.id,
    onCompleted: (data) => {
      if (data?.transactions) {
        const items = data.transactions.items || [];
        setAllTransactions(items);
        setTotal(data.transactions.total || 0);
        if (items.length > 0) {
          // Use the oldest transaction timestamp as the before time for next page
          const oldestTxDate = new Date(items[items.length - 1].transaction.timestamp);
          setBeforeTransactionTime(oldestTxDate.toISOString());
        }
        setHasMore(items.length === limit);
      }
    },
  });

  // Modal state for edit/delete
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // No-op cache updater for token-specific cache (not used on this page)
  const updateCache = () => {};

  const handleEdit = (transaction) => {
    setSelectedTransaction(transaction);
    setIsEditModalOpen(true);
  };

  const handleDelete = (transaction) => {
    setSelectedTransaction(transaction);
    setIsDeleteModalOpen(true);
  };

  // Immediate UI updates
  const handleEditedSaved = (updatedTx) => {
    if (!updatedTx) return;
    setAllTransactions(prev => prev.map(twt => {
      if (twt.transaction?.id === updatedTx.id) {
        return { ...twt, transaction: { ...twt.transaction, ...updatedTx } };
      }
      return twt;
    }));
  };

  const handleDeleted = (deletedId) => {
    if (!deletedId) return;
    setAllTransactions(prev => prev.filter(twt => twt.transaction?.id !== deletedId));
    setTotal(prev => Math.max(0, (prev || 0) - 1));
  };

  // Refetch when limit changes
  useEffect(() => {
    if (currentPortfolio?.id) {
      setAllTransactions([]);
      setBeforeTransactionTime(null);
      setHasMore(true);
      refetch();
    }
  }, [limit, currentPortfolio?.id, refetch]);

  // Handle successful import - reload both transactions and positions
  const handleImportSuccess = async () => {
    try {
      // Reset pagination state
      setBeforeTransactionTime(null);
      setHasMore(true);
      
      // Fetch fresh data and update state manually
      const { data } = await refetch({
        portfolioId: currentPortfolio?.id,
        limit: limit,
      });
      
      // Manually update state with fresh data (same logic as onCompleted)
      if (data?.transactions) {
        const items = data.transactions.items || [];
        setAllTransactions(items);
        setTotal(data.transactions.total || 0);
        if (items.length > 0) {
          const oldestTxDate = new Date(items[items.length - 1].transaction.timestamp);
          setBeforeTransactionTime(oldestTxDate.toISOString());
        }
        setHasMore(items.length === limit);
      }
      
      if (!isStock) reloadPositions();
    } catch (error) {
      console.error('Failed to refresh transactions after import:', error);
    }
  };

  const loadMore = async () => {
    if (!beforeTransactionTime || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const result = await fetchMore({
        variables: {
          portfolioId: currentPortfolio?.id,
          limit: limit,
          beforeDateTime: beforeTransactionTime,
        },
      });

      if (result.data?.transactions) {
        const newTransactions = result.data.transactions.items || [];
        setAllTransactions(prev => [...prev, ...newTransactions]);
        
        if (newTransactions.length > 0) {
          // Use the oldest transaction timestamp as the before time for next page
          const oldestTxDate = new Date(newTransactions[newTransactions.length - 1].transaction.timestamp);
          setBeforeTransactionTime(oldestTxDate.toISOString());
        }
        setHasMore(newTransactions.length === limit);
      }
    } catch (error) {
      console.error('Error loading more transactions:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Group transactions by month
  const groupTransactionsByMonth = (transactions) => {
    const groups = {};
    
    transactions.forEach(transactionWithToken => {
      const monthKey = startOfMonth(new Date(transactionWithToken.transaction.timestamp)).toISOString();
      if (!groups[monthKey]) {
        groups[monthKey] = [];
      }
      groups[monthKey].push(transactionWithToken);
    });

    // Sort months in descending order (newest first)
    return Object.entries(groups)
      .sort(([a], [b]) => new Date(b) - new Date(a))
      .map(([month, transactions]) => ({
        month,
        transactions: transactions.sort((a, b) => {
          const timeA = new Date(a.transaction.timestamp).getTime();
          const timeB = new Date(b.transaction.timestamp).getTime();
          if (timeB !== timeA) return timeB - timeA;
          const createdA = a.transaction.createdAt ? new Date(a.transaction.createdAt).getTime() : 0;
          const createdB = b.transaction.createdAt ? new Date(b.transaction.createdAt).getTime() : 0;
          if (createdB !== createdA) return createdB - createdA;
          const updatedA = a.transaction.updatedAt ? new Date(a.transaction.updatedAt).getTime() : 0;
          const updatedB = b.transaction.updatedAt ? new Date(b.transaction.updatedAt).getTime() : 0;
          if (updatedB !== updatedA) return updatedB - updatedA;
          return (b.transaction.id || '').localeCompare(a.transaction.id || '');
        })
      }));
  };

  const monthlyGroups = groupTransactionsByMonth(allTransactions);

  const selectedPosition = selectedTransaction && !isStock
    ? positions.find(position => position.token?.id === selectedTransaction.tokenId)
    : null;
  const selectedToken = selectedTransaction
    ? allTransactions.find(twt => twt.transaction?.id === selectedTransaction.id)?.token
    : null;

  if (!currentPortfolio) {
    return isStock
      ? <PortfolioRequired title="Stock transactions" description="Please select a portfolio to view stock transactions." />
      : <PortfolioRequired title="Transactions" description="Please select a portfolio to view transactions." />;
  }

  if (loading && allTransactions.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <ErrorAlert message="Failed to load transactions. Please try again." />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Transactions {total > 0 ? `(${total.toLocaleString()})` : ''}
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {isStock ? 'View all stock transactions for' : 'View all transactions for'} {currentPortfolio.name}
            </p>
          </div>

          {isStock && (
            <div className="flex space-x-3 flex-shrink-0">
              <button
                onClick={() => openImportModal({ portfolioId: currentPortfolio?.id, assetKind: 'stock' })}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Import transactions
              </button>
            </div>
          )}

          {!isStock && (
          <div className="flex space-x-3 flex-shrink-0">
            <button
              onClick={() => openImportModal({ portfolioId: currentPortfolio?.id, onSuccess: handleImportSuccess })}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Import
            </button>
            
            {total > 0 && (
              <ExportTransactionsButton 
                portfolioId={currentPortfolio.id} 
                portfolioName={currentPortfolio.name} 
              />
            )}
          </div>
          )}
        </div>
      </div>

      {monthlyGroups.length === 0 && isStock ? (
        <EmptyState
          title="No stock transactions yet"
          description="Import your stock transactions, or open an instrument and record a buy."
          action={() => openImportModal({ portfolioId: currentPortfolio?.id, assetKind: 'stock' })}
          actionLabel="Import transactions"
        />
      ) : monthlyGroups.length === 0 ? (
        <EmptyState
          title="No transactions found"
          description="Import transactions or add them manually to get started."
          action={() => openImportModal({ portfolioId: currentPortfolio?.id, onSuccess: handleImportSuccess })}
          actionLabel="Import Transactions"
        />
      ) : (
        <div className="space-y-6">
          {monthlyGroups.map(({ month, transactions }) => (
            <MonthlyTransactionGroup
              key={month}
              month={month}
              transactions={transactions}
              onEdit={handleEdit}
              onDelete={handleDelete}
              tokenLocations={tokenLocations}
            />
          ))}
          
          {hasMore && (
            <div className="flex justify-between items-center py-6">
              <button
                onClick={loadMore}
                disabled={isLoadingMore}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingMore ? (
                  <>
                    <div className="flex items-center">
                      <LoadingSpinner inline size="sm" color="white" />
                      <span className="ml-2">Loading...</span>
                    </div>
                  </>
                ) : (
                  'Load More'
                )}
              </button>
              
              <div className="flex items-center space-x-2">
                <label htmlFor="limit-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Load:
                </label>
                <select
                  id="limit-select"
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  className="block w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 text-sm"
                >
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {isStock ? (
        <>
          <StockTransactionModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            stockId={selectedToken?.id}
            portfolioId={currentPortfolio?.id}
            stockCurrency={selectedTransaction?.currency}
            transaction={isEditModalOpen ? selectedTransaction : null}
            onSaved={(result) => handleEditedSaved(result?.transaction)}
          />
          <StockDeleteTransactionDialog
            transaction={isDeleteModalOpen ? selectedTransaction : null}
            symbol={selectedToken?.ticker}
            portfolioId={currentPortfolio?.id}
            onClose={() => setIsDeleteModalOpen(false)}
            onDeleted={handleDeleted}
          />
        </>
      ) : (
      <>
      {/* Import modal is provided globally via ImportModalProvider */}
      {/* Edit Transaction Modal */}
      <TransactionModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        transaction={selectedTransaction}
        updateCache={updateCache}
        portfolioId={currentPortfolio?.id}
        refetch={refetch}
        tokenId={selectedTransaction?.tokenId}
        tokenLocations={tokenLocations}
        locationBalances={selectedPosition?.locationBalances || []}
        holdingsAmount={selectedPosition?.holdingsAmount}
        onSaved={handleEditedSaved}
      />

      {/* Delete Transaction Modal */}
      <DeleteTransactionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        transaction={selectedTransaction}
        updateCache={updateCache}
        portfolioId={currentPortfolio?.id}
        refetch={refetch}
        onDeleted={handleDeleted}
      />
      </>
      )}
    </div>
  );
};

export default TransactionsPage; 